/** Moins de 18 ans à la date du jour (date de naissance ISO ou Date). */
export function isMinorFromDateOfBirth(dateOfBirth, isMinorFlag = false) {
  if (isMinorFlag === true) return true;
  if (!dateOfBirth) return false;
  const d = new Date(dateOfBirth);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age < 18;
}

export function getSectorPricing(ticket, sectorName) {
  if (!ticket?.areaPricing || !sectorName) return { price: null, minorPrice: null };

  for (const pricing of ticket.areaPricing) {
    if (pricing.hasSectors && pricing.sectors) {
      const sector = pricing.sectors.find((s) => s.name === sectorName);
      if (sector) {
        const price = sector.price != null ? Number(sector.price) : null;
        const minorPrice =
          sector.minorPrice != null && Number(sector.minorPrice) > 0
            ? Number(sector.minorPrice)
            : null;
        return { price, minorPrice };
      }
    }
  }
  return { price: null, minorPrice: null };
}

export function getAwayMatchPricing(ticket) {
  if (!ticket) return { price: null, minorPrice: null };
  const price = ticket.price != null ? Number(ticket.price) : null;
  const minorPrice =
    ticket.hasMinorPrice && ticket.minorPrice != null && Number(ticket.minorPrice) > 0
      ? Number(ticket.minorPrice)
      : null;
  return { price, minorPrice };
}

export function getBaseTicketPrices(ticket, sectorName) {
  if (ticket?.isHome) {
    return getSectorPricing(ticket, sectorName);
  }
  return getAwayMatchPricing(ticket);
}

export function unitPriceForAttendee(ticket, sectorName, dateOfBirth) {
  const { price, minorPrice } = getBaseTicketPrices(ticket, sectorName);
  if (price == null) return null;
  if (isMinorFromDateOfBirth(dateOfBirth) && minorPrice != null) {
    return minorPrice;
  }
  return price;
}

/**
 * Détail des prix pour le récapitulatif (domicile avec secteur ou extérieur avec prix défini).
 */
export function computeTicketOrderPricing({
  ticket,
  sector,
  showPrimaryTicket,
  primaryDateOfBirth,
  primaryIsMinor,
  includeCompanion,
  companionAttendees,
}) {
  if (!ticket) {
    return { ready: false, total: null, adultCount: 0, minorCount: 0, savings: 0, adultUnit: null, minorUnit: null };
  }

  const { price: adultUnit, minorPrice: minorUnit } = getBaseTicketPrices(ticket, sector);

  if (ticket.isHome && !sector) {
    return { ready: false, total: null, adultCount: 0, minorCount: 0, savings: 0, adultUnit, minorUnit };
  }

  if (adultUnit == null) {
    return { ready: false, total: null, adultCount: 0, minorCount: 0, savings: 0, adultUnit, minorUnit };
  }

  const holders = [];
  if (showPrimaryTicket) {
    holders.push({ dateOfBirth: primaryDateOfBirth, isMinor: primaryIsMinor });
  }
  if (includeCompanion) {
    (companionAttendees || []).forEach((a) => {
      holders.push({ dateOfBirth: a?.dateOfBirth, isMinor: a?.isMinor });
    });
  }

  if (holders.length === 0) {
    return { ready: false, total: null, adultCount: 0, minorCount: 0, savings: 0, adultUnit, minorUnit };
  }

  let adultCount = 0;
  let minorCount = 0;
  let total = 0;

  holders.forEach(({ dateOfBirth, isMinor: minorFlag }) => {
    const isMinor = isMinorFromDateOfBirth(dateOfBirth, minorFlag);
    if (isMinor && minorUnit != null) {
      minorCount += 1;
      total += minorUnit;
    } else {
      adultCount += 1;
      total += adultUnit;
    }
  });

  const savings =
    minorUnit != null && minorCount > 0 ? minorCount * (adultUnit - minorUnit) : 0;

  return {
    ready: true,
    total,
    adultCount,
    minorCount,
    savings,
    adultUnit,
    minorUnit,
    hasMinorTariff: minorUnit != null,
    mixed: minorCount > 0 && adultCount > 0,
    uniformUnitPrice:
      minorCount === 0
        ? adultUnit
        : adultCount === 0 && minorUnit != null
          ? minorUnit
          : null,
    adminFixed: false,
  };
}

/** Prix admin / commandes existantes (attendees en base). */
export function computeOrderTicketPricingFromOrder(ticket, order) {
  const quantity = order?.quantity || 0;
  const empty = {
    ready: false,
    total: null,
    adultCount: 0,
    minorCount: 0,
    savings: 0,
    adultUnit: null,
    minorUnit: null,
    hasMinorTariff: false,
    mixed: false,
    uniformUnitPrice: null,
    adminFixed: false,
  };

  if (!ticket || !quantity) return empty;

  if (order?.unitPrice != null) {
    const unit = Number(order.unitPrice);
    return {
      ready: true,
      total: unit * quantity,
      adultCount: quantity,
      minorCount: 0,
      savings: 0,
      adultUnit: unit,
      minorUnit: null,
      hasMinorTariff: false,
      mixed: false,
      uniformUnitPrice: unit,
      adminFixed: true,
    };
  }

  const attendees = Array.isArray(order?.attendees) ? order.attendees : [];
  const showPrimaryTicket = attendees.some((a) => a.role === 'primary');
  const companionAttendees = attendees.filter((a) => a.role === 'companion');
  const primary = attendees.find((a) => a.role === 'primary');

  return computeTicketOrderPricing({
    ticket,
    sector: order?.sector,
    showPrimaryTicket,
    primaryDateOfBirth: primary?.dateOfBirth,
    primaryIsMinor: false,
    includeCompanion: companionAttendees.length > 0,
    companionAttendees,
  });
}

export function getOrderTotalPriceFromOrder(ticket, order) {
  const p = computeOrderTicketPricingFromOrder(ticket, order);
  if (p.ready && p.total != null) return p.total;
  return null;
}

export function getOrderUnitPriceFromOrder(ticket, order) {
  if (order?.unitPrice != null) return Number(order.unitPrice);
  const p = computeOrderTicketPricingFromOrder(ticket, order);
  if (p.uniformUnitPrice != null) return p.uniformUnitPrice;
  const { price } = getBaseTicketPrices(ticket, order?.sector);
  return price;
}
